// Shared user-directory autocomplete used by CreateRoomDialog,
// NewDmDialog, and InviteDialog. Wraps an input + a results dropdown;
// the caller decides what happens on select (adds to a chip list,
// opens a DM, sends an invite).
//
// The search is debounced (120ms) so a fast typist doesn't fire a
// request per keystroke. `disabledUserIds` hides entries the caller
// doesn't want selectable (already-invited users, self). The row
// height, dropdown width, and row layout come from the brief.

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Search } from "lucide-react";

import { getInitials } from "~/lib/initials";
import type { UserDirectoryEntry } from "~/lib/matrix";
import { type ResolveProfile, useDirectorySearch } from "~/lib/use-directory-search";
import { useUnderline } from "~/lib/underline";
import { ICON_STROKE } from "~/lib/icon";

export interface UserSearchProps {
  /** Placeholder shown in the input when empty. */
  placeholder?: string;
  /** Accessible label for the input. */
  label: string;
  /** Async search against the homeserver directory. */
  onSearch: (term: string) => Promise<UserDirectoryEntry[]>;
  /** Called when the user selects a suggestion. */
  onSelect: (user: UserDirectoryEntry) => void;
  /** Matrix ids to mark as already-picked / ineligible. */
  disabledUserIds?: string[];
  resolveProfile?: ResolveProfile;
  /** Label shown at the right of a disabled row. */
  disabledLabel?: string;
  /** Optional right-side adornment inside each row (e.g. invite button). */
  rowSuffix?: (user: UserDirectoryEntry) => ReactNode;
  /** Focus the input on mount. */
  autoFocus?: boolean;
  /** Controlled input value. When supplied, this component is fully
   *  controlled; otherwise it manages its own term. */
  value?: string;
  onChangeValue?: (next: string) => void;
  /** Whether the dropdown should render its empty-state row. Defaults
   *  to true. */
  showEmptyState?: boolean;
}

export function UserSearch({
  placeholder,
  label,
  onSearch,
  onSelect,
  disabledUserIds,
  resolveProfile,
  disabledLabel,
  rowSuffix,
  autoFocus,
  value,
  onChangeValue,
  showEmptyState = true,
}: UserSearchProps) {
  const [internal, setInternal] = useState("");
  const term = value ?? internal;
  const [highlighted, setHighlighted] = useState(-1);
  const listId = `${useId()}-results`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const underline = useUnderline();
  const { results, excludedResults, loading } = useDirectorySearch(onSearch, term, {
    excludeUserIds: disabledUserIds,
    resolveProfile,
    offerMxid: true,
  });

  useEffect(() => {
    setHighlighted(results.length > 0 ? 0 : -1);
  }, [results]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const setTerm = useCallback(
    (next: string) => {
      if (onChangeValue) onChangeValue(next);
      else setInternal(next);
    },
    [onChangeValue],
  );
  const noop = useCallback(() => undefined, []);

  const trimmed = term.trim();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      const choice = results[highlighted];
      if (choice) {
        e.preventDefault();
        onSelect(choice);
      }
    }
  };

  const inputStyle: CSSProperties = {
    flex: 1,
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    color: "var(--chat-fg-primary)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-md-size)",
    lineHeight: "var(--chat-type-body-md-line)",
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-2)",
          height: "var(--chat-directory-row-height)",
          borderBottom: `1px solid ${underline.borderColor}`,
          background: "transparent",
          transition: "border-color var(--chat-motion-fast) var(--chat-motion-ease)",
        }}
      >
        <Search
          aria-hidden
          size={14}
          strokeWidth={ICON_STROKE}
          style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
        />
        <input
          ref={inputRef}
          type="text"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={underline.onFocus}
          onBlur={underline.onBlur}
          placeholder={placeholder ?? "Search by name or @handle"}
          aria-label={label}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={results.length > 0 || (loading && trimmed.length > 0)}
          role="combobox"
          style={inputStyle}
        />
      </div>
      {trimmed.length > 0 && (
        <div
          id={listId}
          role="listbox"
          aria-label={label}
          style={{
            marginTop: "var(--chat-space-1)",
            maxHeight: "var(--chat-directory-dropdown-max-height)",
            overflowY: "auto",
          }}
        >
          {loading ? (
            <SearchStateRow>Searching…</SearchStateRow>
          ) : results.length === 0 ? (
            showEmptyState && excludedResults.length === 0 ? (
              <SearchStateRow>No people match “{trimmed}”.</SearchStateRow>
            ) : null
          ) : (
            results.map((user, index) => (
              <UserRow
                key={user.userId}
                user={user}
                highlighted={index === highlighted}
                disabled={false}
                onPick={() => onSelect(user)}
                onHover={() => setHighlighted(index)}
                suffix={rowSuffix?.(user)}
              />
            ))
          )}
          {/* Already-picked users still render below the active list so
              the user sees why a search match is missing. */}
          {excludedResults.map((user) => (
            <UserRow
              key={`disabled-${user.userId}`}
              user={user}
              highlighted={false}
              disabled={true}
              disabledLabel={disabledLabel}
              onPick={noop}
              onHover={noop}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SearchStateRow({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        height: "var(--chat-directory-row-height)",
        padding: "0 var(--chat-space-3)",
        display: "flex",
        alignItems: "center",
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
      }}
    >
      {children}
    </div>
  );
}

function UserRow({
  user,
  highlighted,
  disabled,
  disabledLabel,
  onPick,
  onHover,
  suffix,
}: {
  user: UserDirectoryEntry;
  highlighted: boolean;
  disabled: boolean;
  disabledLabel?: string;
  onPick: () => void;
  onHover: () => void;
  suffix?: ReactNode;
}) {
  return (
    <div
      role="option"
      aria-selected={highlighted}
      aria-disabled={disabled || undefined}
      onMouseMove={disabled ? undefined : onHover}
      onMouseDown={(e) => {
        // mousedown, not click — stops the input blurring before we
        // fire the selection handler.
        e.preventDefault();
        if (!disabled) onPick();
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        height: "var(--chat-directory-row-height)",
        padding: "0 var(--chat-space-3)",
        borderRadius: "var(--chat-radius-sm)",
        background: highlighted ? "var(--chat-surface-raised)" : "transparent",
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <UserAvatar name={user.displayName} />
      <div
        data-feedback-redact="true"
        style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column" }}
      >
        <span
          style={{
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {user.displayName}
        </span>
        <span
          style={{
            color: "var(--chat-fg-muted)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {user.userId}
        </span>
      </div>
      {disabled && disabledLabel && (
        <span
          style={{
            color: "var(--chat-fg-muted)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-xs-size)",
          }}
        >
          {disabledLabel}
        </span>
      )}
      {!disabled && suffix}
    </div>
  );
}

export function UserAvatar({ name, size = 24 }: { name: string; size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        borderRadius: "var(--chat-radius-full)",
        background: "var(--chat-surface-raised)",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontWeight: 500,
        fontSize: Math.max(10, Math.round(size * 0.42)),
        letterSpacing: "0.02em",
        flexShrink: 0,
      }}
    >
      {getInitials(name)}
    </span>
  );
}
